import { useEffect, useState } from "react";

import MuiPopover, { PopoverProps } from "@mui/material/Popover";
import Box, { BoxProps } from "@mui/system/Box";

import { Button } from "../Button/Button";
import { ButtonProps } from "../types";
import { Emitter } from "../useEventEmitter";

export interface Props
  extends Omit<PopoverProps, "open" | "anchorEl" | "onClose"> {
  /**
   * Props for the trigger button that opens the popover. Omit this when the
   * popover should instead be anchored to an element you already render
   * elsewhere and opened/closed via the `anchorEl`/`open` props below.
   */
  buttonProps?: Omit<ButtonProps, "onClick"> & { onClick?: () => void };
  /** Externally controlled anchor. Required (and used) when `buttonProps` is omitted. */
  anchorEl?: HTMLElement | null;
  /** Externally controlled open state. Defaults to `Boolean(anchorEl)` when omitted. */
  open?: boolean;
  /** Called when the popover closes, in addition to any internal close handling. */
  onClose?: () => void;
  /** Styles for the content wrapper. Defaults to `{ p: 1 }`; pass `{ p: 0 }` for content that supplies its own padding/background. */
  contentSx?: BoxProps["sx"];
  emitter?: Emitter<void>;
}

export const Popover: React.FC<Props> = (props: Props) => {
  const {
    children,
    buttonProps,
    emitter,
    anchorEl: controlledAnchorEl,
    open: controlledOpen,
    onClose,
    contentSx = { p: 1 },
    ...rest
  } = props;

  const [uncontrolledAnchorEl, setUncontrolledAnchorEl] =
    useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUncontrolledAnchorEl(event.currentTarget);
    buttonProps?.onClick?.();
  };

  const handleClose = () => {
    setUncontrolledAnchorEl(null);
    onClose?.();
  };

  useEffect(() => {
    const unsubscribe = emitter?.subscribe(() => {
      setUncontrolledAnchorEl(null);
    });

    return unsubscribe;
  }, [emitter]);

  const anchorEl =
    controlledAnchorEl !== undefined
      ? controlledAnchorEl
      : uncontrolledAnchorEl;
  const open = controlledOpen ?? Boolean(anchorEl);

  return (
    <>
      <div>
        {buttonProps && (
          <Button
            variant="outlined"
            color="secondary"
            {...buttonProps}
            onClick={handleClick}
          />
        )}
        <MuiPopover
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          sx={{ mt: 0.5 }}
          {...rest}
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
        >
          <Box sx={contentSx}>{children}</Box>
        </MuiPopover>
      </div>
    </>
  );
};
